import * as React from 'react';
import { cn } from '@noor/lib/cn';
import { IconButton } from '../icon-button';
import { Tooltip, TooltipTrigger, TooltipContent } from '../tooltip';

/**
 * Any icon component that accepts a `className` — a `lucide-react` icon or a
 * local CPanel glyph (`cpanel-icons.tsx`) both satisfy it.
 */
type IconComponent = React.ComponentType<{ className?: string }>;

export type AppShellIconRailProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  /** Brand mark pinned to the top of the rail (a 32px mark), above a divider. */
  header?: React.ReactNode;
  /** The stack of icon buttons. */
  children?: React.ReactNode;
  /** Optional content pinned to the bottom of the rail. */
  footer?: React.ReactNode;
};

/**
 * The leftmost ~82px vertical rail: a brand mark up top, a divider, then a
 * stack of icon buttons, with an optional footer pinned to the bottom.
 */
function AppShellIconRail({
  className,
  header,
  children,
  footer,
  ...props
}: AppShellIconRailProps) {
  return (
    <div
      data-slot="app-shell-icon-rail"
      className={cn(
        'flex h-full w-[82px] shrink-0 flex-col items-center gap-1 border-r border-border bg-card pb-4',
        className,
      )}
      {...props}
    >
      {header !== undefined ? (
        <>
          <div
            data-slot="app-shell-icon-rail-header"
            className="flex flex-col items-center px-[24px] py-[16px]"
          >
            {header}
          </div>
          <div aria-hidden="true" className="mb-2 h-px w-[50px] shrink-0 bg-border" />
        </>
      ) : null}
      {children}
      {footer !== undefined ? (
        <div
          data-slot="app-shell-icon-rail-footer"
          className="mt-auto flex flex-col items-center gap-2"
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}

export type AppShellIconRailItemProps = Omit<
  React.ComponentProps<'button'>,
  'aria-label'
> & {
  /**
   * Icon-only glyph rendered inside the button. Omit when using `asChild` and
   * passing your own icon inside the child element.
   */
  icon?: IconComponent;
  /** Accessible name for the icon-only control. Required for a11y. */
  'aria-label': string;
  active?: boolean;
  /**
   * Merge props onto the single child element (a router `<Link>`) instead of
   * rendering a `<button>`, keeping the rail router-agnostic. Follows the same
   * `Slot` contract as `IconButton`/`Button`: pass the link as `children` with
   * the icon inside it.
   */
  asChild?: boolean;
  /**
   * When provided, wraps the button in a Tooltip showing this content to the
   * right. Omit to skip the tooltip entirely.
   */
  tooltip?: React.ReactNode;
};

/**
 * A single icon-only entry in the rail, built on the shared `IconButton`.
 * Inactive rows read ghost/muted; the active row highlights with a neutral gray
 * surface (`bg-muted`, `rounded-2xl`) — not a brand tint. Optionally wrapped in
 * a right-side tooltip.
 *
 * ```tsx
 * <AppShell.IconRailItem icon={InboxIcon} aria-label="Inbox" active />
 * <AppShell.IconRailItem asChild aria-label="Orders" tooltip="Orders">
 *   <Link to="/orders"><PackageIcon className="size-6" /></Link>
 * </AppShell.IconRailItem>
 * ```
 */
function AppShellIconRailItem({
  className,
  icon: Icon,
  active = false,
  asChild = false,
  tooltip,
  children,
  ...props
}: AppShellIconRailItemProps) {
  const button = (
    <IconButton
      data-slot="app-shell-icon-rail-item"
      data-active={active}
      variant="ghost"
      size="default"
      asChild={asChild}
      className={cn(
        'size-12 rounded-xl transition-colors',
        active
          ? 'bg-primary/10 text-primary hover:bg-primary/15 dark:bg-primary/20 dark:text-primary'
          : 'text-[#9ca3af] hover:bg-muted/60 hover:text-foreground',
        className,
      )}
      {...props}
    >
      {asChild ? children : Icon ? <Icon className="size-6" /> : children}
    </IconButton>
  );

  if (tooltip === undefined) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

export { AppShellIconRail, AppShellIconRailItem };
